import React, { useState, useEffect } from 'react';
import { smartComplianceAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, formatDate, getErrorMessage } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Shield, CheckCircle, AlertTriangle, Bell,
  Loader2, Edit, Clock
} from 'lucide-react';

// Phase 27.12 — Friendly-reminder tone. "Missing" is a soft amber nudge
// (Bell icon, "Pending" badge) rather than a red X, so the agent sees
// what's outstanding without feeling nagged. Expired stays amber-red
// because that IS urgent.
const statusConfig = {
  Complete: {
    icon: CheckCircle,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    label: 'Complete',
  },
  Missing: {
    icon: Bell,
    color: 'text-amber-500',
    bg: 'bg-amber-50/70 dark:bg-amber-900/10 border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    label: 'Pending',
  },
  Expired: {
    icon: AlertTriangle,
    color: 'text-orange-600',
    bg: 'bg-orange-50 dark:bg-orange-900/20 border-orange-300',
    badge: 'bg-orange-100 text-orange-700',
    label: 'Expired',
  },
};

// "Because you have <X> contract(s)" hint — copy that turns the
// requirement into a friendly reason instead of a rule.
const reasonFor = (product) => {
  if (!product || product === 'All') return null;
  const nice = {
    Medicare: 'Medicare',
    ACA: 'ACA',
    Life: 'Life',
    Annuity: 'Annuity',
    PSM: 'PSM',
    Dental_Vision: 'Dental / Vision',
  }[product] || product;
  return `Required because you have a ${nice} contract`;
};

export function SmartComplianceTracker({ npn }) {
  const { user } = useAuth();
  const isAdmin = isKRRole(user);
  const canManage = isAdmin || user?.agent_npn === npn;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', expiration_date: '', completed_date: '' });
  const [saving, setSaving] = useState(false);

  // "Mark as Completed" quick prompt — 1-click flow with only the
  // expiration date so the agent doesn't have to think about status.
  const [quickComplete, setQuickComplete] = useState(null);
  const [quickExpirationDate, setQuickExpirationDate] = useState('');
  const [markingComplete, setMarkingComplete] = useState(false);

  useEffect(() => {
    if (npn) loadCompliance();
  }, [npn]);

  const loadCompliance = async () => {
    try {
      const res = await smartComplianceAPI.get(npn);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load compliance:', err);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      status: item.status,
      expiration_date: item.expiration_date || '',
      completed_date: item.completed_date || '',
    });
  };

  const openQuickComplete = (item) => {
    setQuickComplete(item);
    setQuickExpirationDate('');
  };

  const handleQuickComplete = async () => {
    if (!quickExpirationDate) {
      toast.error('Please enter an expiration date');
      return;
    }
    setMarkingComplete(true);
    try {
      await smartComplianceAPI.markComplete(npn, quickComplete.code, {
        expiration_date: quickExpirationDate,
      });
      toast.success(`${quickComplete.name} marked as complete`);
      setQuickComplete(null);
      setQuickExpirationDate('');
      loadCompliance();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to mark complete'));
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await smartComplianceAPI.update(npn, editItem.code, editForm);
      toast.success(`${editItem.name} updated`);
      setEditItem(null);
      loadCompliance();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-slate-400">
        <Shield size={32} className="mb-2" />
        <p className="text-sm">No compliance requirements (no active contracts)</p>
      </div>
    );
  }

  return (
    <div data-testid="smart-compliance-tracker">
      {/* Summary Bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {data.completed}/{data.total} complete
          </span>
          <Badge className={data.compliance_rate === 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
            {data.compliance_rate}%
          </Badge>
        </div>
        {data.active_products.length > 0 && (
          <div className="flex gap-1">
            {data.active_products.map(p => (
              <Badge key={p} variant="outline" className="text-[10px]">{p.replace(/_/g, '/')}</Badge>
            ))}
          </div>
        )}
      </div>
      <Progress value={data.compliance_rate} className="h-2 mb-4" />

      {/* Compliance Items */}
      <div className="space-y-3">
        {data.compliance_items.map((item) => {
          const config = statusConfig[item.status] || statusConfig.Missing;
          const StatusIcon = config.icon;
          const isPending = item.status === 'Missing' || item.status === 'Expired';
          const reason = reasonFor(item.product);

          return (
            <div
              key={item.code}
              className={`flex items-center justify-between p-3.5 rounded-lg border ${config.bg}`}
              data-testid={`compliance-item-${item.code}`}
            >
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <StatusIcon size={18} className={`${config.color} mt-0.5 shrink-0`} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{item.name}</p>
                    <Badge className={`text-[10px] ${config.badge}`} data-testid={`compliance-status-${item.code}`}>
                      {config.label}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                  {isPending && reason && (
                    <p className="text-[11px] italic text-amber-700 mt-1" data-testid={`compliance-reason-${item.code}`}>
                      {reason}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="text-[10px] text-slate-400">Product: {item.product}</span>
                    {item.expiration_date && (
                      <span className="text-[10px] text-slate-400 flex items-center gap-1">
                        <Clock size={10} /> Expires: {formatDate(item.expiration_date)}
                      </span>
                    )}
                    {item.completed_date && (
                      <span className="text-[10px] text-slate-400">Completed: {formatDate(item.completed_date)}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 shrink-0 ml-2">
                {isPending && canManage && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs bg-white hover:bg-amber-50 border-amber-300 text-amber-800"
                    onClick={() => openQuickComplete(item)}
                    data-testid={`mark-complete-${item.code}`}
                  >
                    <CheckCircle size={12} className="mr-1" />
                    Mark Completed
                  </Button>
                )}
                {canManage && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(item)}
                    className="shrink-0 h-8 w-8 p-0"
                    title="Edit full record"
                    data-testid={`edit-compliance-${item.code}`}
                  >
                    <Edit size={13} />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick "Mark as Completed" prompt — only asks for expiration date */}
      <Dialog open={!!quickComplete} onOpenChange={(v) => { if (!v && !markingComplete) { setQuickComplete(null); setQuickExpirationDate(''); } }}>
        <DialogContent className="max-w-sm" data-testid="quick-complete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Mark {quickComplete?.name} as Completed</DialogTitle>
            <DialogDescription>
              When does this certification expire? We&apos;ll set today as the completion date automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="quick-exp-date">Expiration Date *</Label>
            <Input
              id="quick-exp-date"
              type="date"
              value={quickExpirationDate}
              onChange={(e) => setQuickExpirationDate(e.target.value)}
              data-testid="quick-complete-expiration-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setQuickComplete(null); setQuickExpirationDate(''); }} disabled={markingComplete}>
              Cancel
            </Button>
            <Button
              onClick={handleQuickComplete}
              disabled={markingComplete || !quickExpirationDate}
              data-testid="quick-complete-save-btn"
            >
              {markingComplete ? <Loader2 size={13} className="animate-spin mr-1" /> : <CheckCircle size={13} className="mr-1" />}
              Mark Completed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Edit Dialog — status + dates (for admin corrections) */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update {editItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Complete">Complete</SelectItem>
                  <SelectItem value="Missing">Pending</SelectItem>
                  <SelectItem value="Expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Completed Date</Label>
              <Input
                type="date"
                value={editForm.completed_date}
                onChange={(e) => setEditForm(f => ({ ...f, completed_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Expiration Date</Label>
              <Input
                type="date"
                value={editForm.expiration_date}
                onChange={(e) => setEditForm(f => ({ ...f, expiration_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
